import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { NumericFormat } from 'react-number-format';
import { claimPaymentActions, httpRequestsOnSuccessActions } from 'store';
import { FindLoad, FindSuccessItem, formatNumberToFix, renderClientName, useModal, claimEnums } from 'utils';
import {
    AccordionFullTable,
    CustomDelete,
    DateTypeSelector,
    MarkSecondaryBilling,
    ModalContentWrapper,
    NotError,
    SelectAutocompletePagination,
    SingleSearch,
} from 'components';
import { claimPaymentTableInfo } from './constants';
import { claimPostStyles } from '../../styles';
import { AppealInputs } from './appealInputs';

const EDIT_CLAIM_ACTION_TYPE = 'EDIT_CLAIM_PAYMENT_INFO';
const PAY_IN_FULL_CLAIM = 'PAY_IN_FULL_CLAIM';
const PAY_IN_FULL_CLAIM_BILLING = 'PAY_IN_FULL_CLAIM_BILLING';

const errorMessages = {
    // paidIsGreat: 'Allowed amount cannot exceed Paid amount.',
    // notAllowed: 'Please ensure that the total sum of the Deductible, Copay, and Coinsurance amounts entered do not exceed the Allowed AMT.',
    paidIsGreat: 'Paid amount cannot exceed Allowed.',
    balanceError: 'PaidAMT cannot exceed the Balance.',
    notAllowed: 'PaidAMT cannot exceed Pmt Open Amount.',
    openAmtNotAllowed: 'The total sum of Deduct., Copay, CoINS and PaidAMT cannot exceed the Allowed amount.',
};

const errorEnums = {
    openAmtNotAllowed: 'openAmtNotAllowed',
    paidIsGreat: 'paidIsGreat',
    notAllowed: 'notAllowed',
    balanceError: 'balanceError',
};

export const SelectClaimPayment = ({ reqParams }) => {
    const claimsList = useSelector((state) => state.claimPayment.claimPayments);
    const claimPaymentsFiltered = useSelector((state) => state.claimPayment.claimPaymentsFiltered);
    const remittance = useSelector((state) => state.claimPayment.remittanceById);
    const dispatch = useDispatch();
    const singleId = remittance?.id;
    const openAmt = remittance?.openAmount;
    const [activeRow, setActiveRow] = useState();
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const checkError = error;
    const loadSave = FindLoad(EDIT_CLAIM_ACTION_TYPE);
    const successEdit = FindSuccessItem(EDIT_CLAIM_ACTION_TYPE);
    const { open, close } = useModal();
    const classes = claimPostStyles();
    const currentInput = useRef();
    const [sendParams, setSendParams] = useState({});
    const renderParams = () => {
        const sendInfo = {
            ...sendParams,
        };
        delete sendInfo.page;
        sendInfo.limit = 50;
        sendInfo.skip = sendParams?.skip ? sendParams?.skip : 0;
        sendInfo.status = claimEnums.SUBMITTED;
        // sendInfo.clearinghouseStatus = 'ACCEPTED';

        sendInfo.payer = remittance?.payerId;

        delete sendInfo.tabType;
        return sendInfo;
    };

    useEffect(() => {
        getClaimPayments();
    }, [sendParams]);

    const getClaimPayments = (loading) => {
        dispatch(claimPaymentActions.getClaimPaymentFiltered(singleId, renderParams(), loading));
    };

    const afterSuccess = () => {
        getClaimPayments('noLoad');
    };

    useEffect(() => {
        if (successEdit) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_CLAIM_ACTION_TYPE));
            setActiveRow(null);
        }
    }, [successEdit]);

    const notifyOpenAmtError = () => {
        toast(<NotError type={'error'} errMessage={errorMessages.notAllowed} />);
    };
    const handleAction = (item, name, parent) => {
        if (name === 'edit') {
            setActiveRow(item.id);
            const claimBills = [];
            item?.bills.forEach((i) => {
                claimBills.push({
                    id: i?.id,
                    allowedAMT: i?.allowedAMT,
                    deductible: i?.deductible,
                    copay: i?.copay,
                    coIns: i?.coIns,
                    paidAMT: i?.paidByThis,
                    payorPaid: i?.payorPaid,
                    payorBalance: i?.payorBalance,
                });
            });
            setInputs(claimBills);
            currentInput?.current?.focus();
        }
        if (name === 'save') {
            const body = {
                claimId: item?.id,
                remittanceId: remittance?.id,
                body: {
                    claimBills: inputs?.filter((i) => i?.claimStatus !== claimEnums.COMPLETE),
                },
            };
            if (!error) {
                dispatch(claimPaymentActions.editClaimPaymentInfo(body, renderParams(), singleId, reqParams));
            } else {
                toast(<NotError type={'error'} errMessage={errorMessages?.[error]} />);
            }
        }
        if (name === 'close') {
            setActiveRow(null);
            setError(null);
        }

        if (name === 'appeal') {
            open(
                <ModalContentWrapper
                    textAlign={'start'}
                    onClose={close}
                    titleContent={'Appeal this claim?'}
                    subtitleContent={'Please provide a reason for appealing this claim.'}
                >
                    <AppealInputs
                        remittanceId={remittance?.id}
                        renderParams={renderParams()}
                        closeModal={() => {
                            close();
                        }}
                        claimId={item?.id}
                    />
                </ModalContentWrapper>,
                {
                    styles: {
                        bodyStyle: { padding: 0 },
                    },
                }
            );
        }
        if (name === 'payInFull') {
            if (openAmt >= item?.remaining) {
                open(
                    <CustomDelete
                        text="Are you sure you want to pay in full this claim?"
                        info="Pay in full"
                        afterSuccess={close}
                        handleDel={() => {
                            const body = {
                                id: item?.id,
                                remittanceId: remittance?.id,
                            };
                            dispatch(claimPaymentActions.payInFullClaim(body, renderParams(), singleId, reqParams));
                        }}
                        handleClose={close}
                        actionType={PAY_IN_FULL_CLAIM}
                        color={'blue'}
                        innerText={'Confirm'}
                    />
                );
            } else {
                notifyOpenAmtError();
            }
        }
        if (name === 'payInFullBill') {
            if (openAmt >= item?.payorBalance) {
                open(
                    <CustomDelete
                        text="Are you sure you want to pay in full this bill?"
                        info="Pay this bill"
                        handleDel={() => {
                            const body = {
                                id: parent?.id,
                                billId: item?.id,
                                remittanceId: remittance?.id,
                            };
                            dispatch(claimPaymentActions.payInFullClaimBill(body, renderParams(), singleId, reqParams));
                        }}
                        handleClose={close}
                        actionType={PAY_IN_FULL_CLAIM_BILLING}
                        afterSuccess={() => {
                            setInputs({});
                            setActiveRow(null);
                            setError('');
                        }}
                        color={'blue'}
                        innerText={'Confirm'}
                    />
                );
            } else {
                notifyOpenAmtError();
            }
        }

        // if (name === 'secondaryBilling') {
        //     open(
        //         <MarkSecondaryBilling
        //             afterSuccess={() => getClaimPayments('noLoad')}
        //             mark={!item?.secondaryBilling?.secondaryBillingState}
        //             claim={item}
        //         />
        //     );
        // }
    };

    const handleChange = (e, key, item) => {
        const allObj = [...inputs];
        const current = allObj[key];
        const value = e.target.value;
        const convertedValue = value ? parseFloat(value.replace(/[$,]/g, '')) : 0;
        const deductible = e?.target?.name === 'deductible' ? convertedValue : current?.deductible;
        const copay = e?.target?.name === 'copay' ? convertedValue : current?.copay;
        const coIns = e?.target?.name === 'coIns' ? convertedValue : current?.coIns;
        const paidAMT = e?.target?.name === 'paidAMT' ? convertedValue : current?.paidAMT;
        const allowed = e?.target?.name === 'allowedAMT' ? convertedValue : current?.allowedAMT;

        setError('');
        allObj[key][e?.target?.name] = convertedValue;
        setInputs(allObj);

        const enteredValues = Number(deductible + copay + coIns + paidAMT).toFixed(2);
        const totalWithPayor = Number(enteredValues + (current?.payorPaid || 0)).toFixed(2);
        const calculatedBalance = Number(
            item?.allowedAMT >= allowed
                ? item?.payorBalance + item?.paidByThis - (item.allowedAMT - allowed)
                : item?.payorBalance + item?.paidByThis + (allowed - item.allowedAMT)
        ).toFixed(2);

        if (totalWithPayor > allowed) {
            setError(errorEnums.openAmtNotAllowed);
        } else if (paidAMT > Number(openAmt + item?.paidByThis).toFixed(2)) {
            setError(errorEnums.notAllowed);
        } else if (paidAMT > calculatedBalance) {
            setError(errorEnums.balanceError);
        } else {
            setError(null);
        }
    };

    return (
        <div className="select-claim-payment-modal">
            <div className="space-between" style={{ marginTop: '-8px' }}>
                <p className="modal-header-title">
                    {remittance?.displayId ? `Adding to Payment:${remittance?.displayId}` : 'Select Claims to Add'}
                </p>
                <div className="select-claim-filters flex-align-end" style={{ gap: 16 }}>
                    <SingleSearch
                        placeholder={'Search by Claim ID'}
                        searchTerm={sendParams?.searchValue}
                        setSearchTerm={(e) => {
                            const params = {
                                ...sendParams,
                            };
                            if (e) {
                                params.searchKey = 'DISPLAY_ID';
                                params.searchValue = e;
                            } else {
                                delete params['searchKey'];
                                delete params['searchValue'];
                            }
                            setSendParams(params);
                        }}
                    />
                    <SelectAutocompletePagination
                        outLabel={'Client'}
                        name={'client'}
                        handleSelect={(e) => {
                            const params = {
                                ...sendParams,
                            };
                            e?.target?.value ? (params.client = e?.target?.value) : delete params['client'];
                            setSendParams(params);
                        }}
                        placeholder={'All'}
                        defaultValue={sendParams?.client}
                        renderValue={(i) => renderClientName(i)}
                        type={'client'}
                        uiType={'tableFilter'}
                        noError={true}
                    />
                    <DateTypeSelector
                        startName={'from'}
                        endName={'to'}
                        outLabel={'DOS'}
                        filters={sendParams}
                        handleGetDates={(e) => {
                            const params = {
                                ...sendParams,
                                from: e?.from,
                                to: e?.to,
                            };
                            setSendParams(params);
                        }}
                    />
                </div>
            </div>

            <div className="flex-align-center" style={{ gap: 16, marginTop: 6 }}>
                <div className="billing-total-row">
                    <p className="billing-total-title">Total AMT:</p>
                    <div className="billing-total-price-row">
                        <NumericFormat
                            // value={remittance?.totalAmount ? remittance?.totalAmount.toFixed(3) : 0}
                            value={remittance?.totalAmount ? formatNumberToFix(remittance?.totalAmount) : 0}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'$'}
                        />
                    </div>
                </div>
                <div className="billing-total-row">
                    <p className="billing-total-title">Applied AMT:</p>
                    <div className="billing-total-price-row">
                        <NumericFormat
                            // value={remittance?.appliedAmount ? remittance?.appliedAmount.toFixed(3) : 0}
                            value={remittance?.appliedAmount ? formatNumberToFix(remittance?.appliedAmount) : 0}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'$'}
                        />
                    </div>
                </div>
                <div className="billing-total-row">
                    <p className="billing-total-title">Open AMT:</p>
                    <div className="billing-total-price-row">
                        <NumericFormat
                            // value={remittance?.openAmount ? remittance?.openAmount.toFixed(3) : 0}
                            value={remittance?.openAmount ? formatNumberToFix(remittance?.openAmount) : 0}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'$'}
                        />
                    </div>
                </div>
            </div>
            <AccordionFullTable
                getParams={(ev) => setSendParams(ev)}
                savedParams={sendParams}
                handleAccordion={() => {
                    setError('');
                    setActiveRow(null);
                }}
                defaultOpen={activeRow && claimPaymentsFiltered?.claims?.findIndex((item) => item?.id === activeRow)}
                margin={'16px 0 0 0'}
                tableInfo={claimPaymentTableInfo(
                    classes,
                    handleAction,
                    handleChange,
                    currentInput,
                    activeRow,
                    inputs,
                    loadSave,
                    checkError,
                    afterSuccess
                )}
                list={claimPaymentsFiltered?.claims}
                listCount={claimPaymentsFiltered?.count}
            />
        </div>
    );
};
